// PostulationFormModal.tsx - MODAL flotante, portal y diseño consistente

import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FaTimes, FaUser, FaFileUpload, FaSpinner } from "react-icons/fa";
import { VacantePublica, DatosPostulacion, PostulationFormModalProps } from "./types";
import { useJobBoardView } from "./hooks/useJobBoardView";
import { useToast } from "../Globals/Hooks/useToast";
import Swal from "sweetalert2";
import  "./styles.css";
export const PostulationFormModal: React.FC<PostulationFormModalProps> = ({
  isOpen,
  onClose,
  vacante,
  onSuccess,
}) => {
  const { showToast, ToastComponent } = useToast();
  const { enviarPostulacion } = useJobBoardView();
  const [formData, setFormData] = useState({
    nombre_completo: "",
    genero: "" as "Masculino" | "Femenino" | "Otro" | "",
    fecha_nacimiento: "",
    email: "",
    telefono: "",
    ultimo_grado_estudios: "",
    titulo_profesional: "",
    universidad: "",
  });
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [periodoError, setPeriodoError] = useState("");

  // Bloquear scroll global al abrir modal
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [isOpen]);

  if (!isOpen) return null;

  /*** VALIDACIÓN Y UX ***/
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.nombre_completo.trim())
      newErrors.nombre_completo = "El nombre completo es obligatorio";
    if (!formData.genero) newErrors.genero = "El género es obligatorio";
    if (!formData.fecha_nacimiento) {
      newErrors.fecha_nacimiento = "La fecha de nacimiento es obligatoria";
    } else {
      const fechaNacimiento = new Date(formData.fecha_nacimiento);
      const hoy = new Date();
      const edad = hoy.getFullYear() - fechaNacimiento.getFullYear();
      const mesActual = hoy.getMonth();
      const mesNacimiento = fechaNacimiento.getMonth();
      if (fechaNacimiento > hoy) {
        newErrors.fecha_nacimiento = "La fecha de nacimiento no puede ser futura";
      } else if (edad < 18 || (edad === 18 && mesActual < mesNacimiento)) {
        newErrors.fecha_nacimiento = "Debes ser mayor de 18 años para postularte";
      }
    }
    if (!formData.email.trim()) newErrors.email = "El email es obligatorio";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "El formato del email no es válido";
    if (!formData.telefono.trim()) newErrors.telefono = "El teléfono es obligatorio";
    if (!formData.ultimo_grado_estudios.trim())
      newErrors.ultimo_grado_estudios = "El último grado de estudios es obligatorio";
    if (!cvFile) newErrors.cv_file = "El CV en formato PDF es obligatorio";
    else if (cvFile.type !== "application/pdf")
      newErrors.cv_file = "El CV debe ser un archivo PDF";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCvFile(file);
      if (errors.cv_file) setErrors((prev) => ({ ...prev, cv_file: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !cvFile) return;
    setPeriodoError("");
    const ahora = new Date();
    const fechaActual = `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}-${String(ahora.getDate()).padStart(2, '0')}`;
    const horaActual = `${ahora.getHours().toString().padStart(2, '0')}:${ahora.getMinutes().toString().padStart(2, '0')}`;
    const { fecha_registro_inicio, fecha_registro_fin, hora_registro_inicio, hora_registro_fin } = vacante;
    const fueraDelPeriodo =
      fechaActual < fecha_registro_inicio ||
      fechaActual > fecha_registro_fin ||
      (fechaActual === fecha_registro_inicio && horaActual < hora_registro_inicio) ||
      (fechaActual === fecha_registro_fin && horaActual > hora_registro_fin);
    if (fueraDelPeriodo) {
      setPeriodoError("La vacante no está disponible para postulaciones en este momento.");
      return;
    }
    setLoading(true);
    try {
      const datosPostulacion: DatosPostulacion = {
        job_board_id: vacante.job_board_id,
        ...formData,
        genero: formData.genero as "Masculino" | "Femenino" | "Otro",
        cv_file: cvFile,
      };
      const response = await enviarPostulacion(datosPostulacion);
      if (response.success) {
        if (response.data?.mail_error) {
          console.error(
            "[Postulación] La postulación se registró, pero el correo de confirmación NO se pudo enviar:",
            response.data.mail_error
          );
        }
        const genero = formData.genero;
        const tratamiento = genero === "Femenino" ? "a" : genero === "Masculino" ? "o" : "/a";
        const nombreCompleto = formData.nombre_completo;
        const denominacion = vacante.denominacion_funcional;
        const email = formData.email;
        const telefono = formData.telefono;
        resetForm();
        onClose();
        onSuccess();
        await Swal.fire({
          icon: "success",
          title: `Estimad${tratamiento} ${nombreCompleto}`,
          html: `
            <p>En nombre de la <strong>Junta de Agua Potable, Drenaje, Alcantarillado y Saneamiento del Municipio de Irapuato (JAPAMI)</strong>, queremos agradecerte sinceramente por tu interés en formar parte de nuestra institución y por tomarte el tiempo de postularte a la vacante de <strong>${denominacion}</strong>.</p>
            <p>Tu participación es muy importante para nosotros. En JAPAMI trabajamos cada día con el compromiso de brindar servicios esenciales a la comunidad irapuatense, y sabemos que detrás de ese esfuerzo está el talento y la dedicación de personas como tú.</p>
            <p>Hemos recibido tu solicitud correctamente y nuestro equipo de Recursos Humanos se encuentra revisando cada perfil con atención.</p>
            <p>Por favor <strong>espera a que te contactemos</strong> a través de los siguientes medios antes de presentarte a cualquier siguiente paso:</p>
            <p style="background:#f0f4ff;border-left:4px solid #3945DE;padding:10px 14px;border-radius:4px;text-align:left;">
              📧 <strong>Correo:</strong> ${email}<br/>
              📞 <strong>Teléfono:</strong> ${telefono}
            </p>
            <p>Una vez que recibas nuestra comunicación, te indicaremos los pasos a seguir en el proceso de selección.</p>
            <p>Te deseamos mucho ánimo en este proceso. <strong>¡Tu iniciativa ya es un gran primer paso!</strong></p>
            <p style="margin-top:16px;"><em>Cordialmente,<br/>Departamento de Recursos Humanos<br/>Junta de Agua Potable, Drenaje, Alcantarillado y Saneamiento del Municipio de Irapuato (JAPAMI)</em></p>
          `,
          confirmButtonText: "Entendido",
          confirmButtonColor: "#3945DE",
          width: "600px",
        });
      } else {
        showToast(response.message, "error");
      }
    } catch (error: any) {
      showToast(error?.message || "Ocurrió un error al procesar tu postulación. Inténtalo nuevamente.", "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nombre_completo: "",
      genero: "",
      fecha_nacimiento: "",
      email: "",
      telefono: "",
      ultimo_grado_estudios: "",
      titulo_profesional: "",
      universidad: "",
    });
    setCvFile(null);
    setErrors({});
    setPeriodoError("");
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  // El contenido del modal en Portal
  return createPortal(
    <>
      <div className="modal-fullscreen-overlay" onClick={handleOverlayClick}>
        <div className="modal-fullscreen-content">
          <button className="modal-close-btn" onClick={onClose} type="button" aria-label="Cerrar modal">
            <FaTimes />
          </button>
          <div className="modal-fullscreen-title">
            <FaUser style={{ marginRight: 10, color: "#3945DE" }} />
            Registro de postulación
          </div>
          <div className="modal-subtitle">
            <b>Vacante:</b> {vacante.denominacion_funcional} &nbsp;|&nbsp;
            <b>Folio:</b> {vacante.folio_vacante}
          </div>
          <form onSubmit={handleSubmit} className="postulation-form">
            {/* Información Personal */}
            <section className="job-section">
              <h3 className="job-section-title">Información Personal</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Nombre Completo *</label>
                  <input
                    type="text"
                    name="nombre_completo"
                    value={formData.nombre_completo}
                    onChange={handleInputChange}
                    className={errors.nombre_completo ? "error" : ""}
                    placeholder="Escribe tu nombre completo"
                  />
                  {errors.nombre_completo && <span className="error-text">{errors.nombre_completo}</span>}
                </div>
                <div className="form-group">
                  <label>Género *</label>
                  <select
                    name="genero"
                    value={formData.genero}
                    onChange={handleInputChange}
                    className={errors.genero ? "error" : ""}
                  >
                    <option value="">Selecciona tu género</option>
                    <option value="Masculino">Masculino</option>
                    <option value="Femenino">Femenino</option>
                    <option value="Otro">Otro</option>
                  </select>
                  {errors.genero && <span className="error-text">{errors.genero}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Fecha de Nacimiento *</label>
                  <input
                    type="date"
                    name="fecha_nacimiento"
                    value={formData.fecha_nacimiento}
                    onChange={handleInputChange}
                    className={errors.fecha_nacimiento ? "error" : ""}
                  />
                  {errors.fecha_nacimiento && <span className="error-text">{errors.fecha_nacimiento}</span>}
                </div>
                <div className="form-group">
                  <label>Teléfono *</label>
                  <input
                    type="tel"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleInputChange}
                    className={errors.telefono ? "error" : ""}
                    placeholder="10 dígitos (ej: 4621234567)"
                  />
                  {errors.telefono && <span className="error-text">{errors.telefono}</span>}
                </div>
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? "error" : ""}
                  placeholder="tu-email@ejemplo.com"
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>
            </section>

            {/* Formación Académica */}
            <section className="job-section">
              <h3 className="job-section-title">Formación Académica</h3>
              <div className="form-group">
                <label>Último Grado de Estudios *</label>
                <input
                  type="text"
                  name="ultimo_grado_estudios"
                  value={formData.ultimo_grado_estudios}
                  onChange={handleInputChange}
                  className={errors.ultimo_grado_estudios ? "error" : ""}
                  placeholder="Ej: Licenciatura, Ingeniería, etc."
                />
                {errors.ultimo_grado_estudios && <span className="error-text">{errors.ultimo_grado_estudios}</span>}
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Título Profesional (Opcional)</label>
                  <input
                    type="text"
                    name="titulo_profesional"
                    value={formData.titulo_profesional}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Ej: Lic. en Administración, Ing. en Sistemas"
                  />
                </div>
                <div className="form-group">
                  <label>Universidad (Opcional)</label>
                  <input
                    type="text"
                    name="universidad"
                    value={formData.universidad}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Nombre de la institución educativa"
                  />
                </div>
              </div>
            </section>

            {/* Documentación */}
            <section className="job-section">
              <h3 className="job-section-title">Documentación</h3>
              <div className="form-group">
                <label>Currículum Vitae (PDF) *</label>
                <label htmlFor="cv-upload" className={`btn-upload ${errors.cv_file ? "error" : ""}`}>
                  <FaFileUpload />
                  {cvFile ? cvFile.name : "Seleccionar archivo PDF"}
                </label>
                <input
                  id="cv-upload"
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                {errors.cv_file && <span className="error-text">{errors.cv_file}</span>}
                <div style={{ fontSize: "0.9rem", color: "#666" }}>
                  Tamaño máximo: 5MB. Solo se aceptan archivos PDF.
                </div>
              </div>
            </section>

            {/* Error de periodo de registro */}
            {periodoError && (
              <div className="periodo-error-banner">
                {periodoError}
              </div>
            )}
            {/* Botones */}
            <div className="modify-buttons">
              <button
                type="button"
                onClick={onClose}
                className="modify-btn-cancel"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="modify-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <FaSpinner className="spinner" />
                    Enviando...
                  </>
                ) : (
                  "Enviar postulación"
                )}
              </button>
            </div>
          </form>
      <ToastComponent />
        </div>
      </div>
    </>,
    document.body
  );
};
